import type { FormEvent } from 'react'
import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { API_ORIGIN } from '@/constants'

export type ApplyQuestion = {
  id: string
  question_text: string
  question_type: string
  is_required: boolean
  order: number
  config: Record<string, unknown>
}

export type ApplyContext = {
  opportunity: {
    id: string
    slug: string
    title: string
    description: string
    location: string
    organisation_name: string
    organisation_slug: string
  }
  questions: ApplyQuestion[]
}

export type ExternalApplyContextState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'ready'; context: ApplyContext }
  | { status: 'missing'; message: string }
  | { status: 'error'; message: string }

async function readJson(response: Response): Promise<Record<string, unknown>> {
  try {
    const data = await response.json()
    if (data && typeof data === 'object') return data as Record<string, unknown>
  } catch {
    /* HTML error pages and empty bodies */
  }
  return {}
}

function errorText(data: Record<string, unknown>, fallback: string): string {
  return typeof data.error === 'string' && data.error.trim() ? data.error : fallback
}

export function useExternalApplyContext(jobId: string | undefined): ExternalApplyContextState {
  const [state, setState] = useState<ExternalApplyContextState>(() =>
    jobId ? { status: 'loading' } : { status: 'idle' },
  )

  useEffect(() => {
    let cancelled = false
    async function load() {
      if (!jobId) {
        if (!cancelled) setState({ status: 'idle' })
        return
      }
      setState({ status: 'loading' })
      try {
        const response = await fetch(
          `${API_ORIGIN}/api/applications/external/apply/${encodeURIComponent(jobId)}`,
        )
        const data = await readJson(response)
        if (cancelled) return
        if (response.status === 404) {
          setState({ status: 'missing', message: errorText(data, 'Could not load this job') })
          return
        }
        if (!response.ok) {
          setState({ status: 'error', message: errorText(data, 'Could not load this job') })
          return
        }
        setState({ status: 'ready', context: data as unknown as ApplyContext })
      } catch (err) {
        if (!cancelled) {
          setState({
            status: 'error',
            message: err instanceof Error ? err.message : 'Could not load this job',
          })
        }
      }
    }
    void load()
    return () => {
      cancelled = true
    }
  }, [jobId])

  return state
}

export type ExternalApplyFormApi = {
  answers: Record<string, string>
  setAnswer: (questionId: string, value: string) => void
  coverLetter: string
  setCoverLetter: (value: string) => void
  setResumeFile: (file: File | null) => void
  email: string
  setEmail: (value: string) => void
  firstName: string
  setFirstName: (value: string) => void
  lastName: string
  setLastName: (value: string) => void
  isSubmitting: boolean
  error: string | null
  submittedEmail: string | null
  questions: ApplyQuestion[]
  handleSubmit: (event: FormEvent) => void
}

export function useExternalApplyForm(
  jobId: string | undefined,
  context: ApplyContext | null,
): ExternalApplyFormApi {
  const [searchParams] = useSearchParams()
  const [answers, setAnswers] = useState<Record<string, string>>({})
  const [coverLetter, setCoverLetter] = useState('')
  const [resumeFile, setResumeFile] = useState<File | null>(null)
  const [email, setEmail] = useState('')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [submittedEmail, setSubmittedEmail] = useState<string | null>(null)

  const sourceMetadata = useMemo(() => {
    const source = searchParams.get('source') || 'external_board'
    const provider = searchParams.get('provider') || source
    const listingRef = searchParams.get('listing_ref') || ''
    const attribution = Object.fromEntries(
      Array.from(searchParams.entries()).filter(([key]) => key.startsWith('utm_')),
    )
    return { source, provider, listingRef, attribution }
  }, [searchParams])

  async function handleSubmit(event: FormEvent) {
    event.preventDefault()
    if (!jobId || !context) return

    setIsSubmitting(true)
    setError(null)
    try {
      const payload = {
        email,
        first_name: firstName,
        last_name: lastName,
        source: sourceMetadata.source,
        provider: sourceMetadata.provider,
        listing_ref: sourceMetadata.listingRef,
        attribution: sourceMetadata.attribution,
        answers: (context.questions ?? []).map((question) => ({
          question_id: question.id,
          answer_text: answers[question.id] || '',
          answer_options: [],
        })),
        cover_letter: coverLetter.trim(),
      }
      const body = new FormData()
      body.append('payload', JSON.stringify(payload))
      if (resumeFile) body.append('resume', resumeFile)
      const response = await fetch(
        `${API_ORIGIN}/api/applications/external/apply/${encodeURIComponent(jobId)}`,
        { method: 'POST', body },
      )
      const data = await readJson(response)
      if (!response.ok) throw new Error(errorText(data, 'Could not start this application'))
      setSubmittedEmail(typeof data.email === 'string' && data.email ? data.email : email)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not start this application')
    } finally {
      setIsSubmitting(false)
    }
  }

  return {
    answers,
    setAnswer: (questionId, value) => setAnswers((current) => ({ ...current, [questionId]: value })),
    coverLetter,
    setCoverLetter,
    setResumeFile,
    email,
    setEmail,
    firstName,
    setFirstName,
    lastName,
    setLastName,
    isSubmitting,
    error,
    submittedEmail,
    questions: context?.questions ?? [],
    handleSubmit,
  }
}

